import axios from 'axios';
import { 
    API_BASE_URL, 
    API_TIMEOUT, 
    API_RETRY_ATTEMPTS, 
    API_RETRY_DELAY, 
    DEFAULT_HEADERS,
    DEBUG_MODE 
} from '../config/apiConfig';
import authService from './auth.service';
import toast from 'react-hot-toast';

/**
 * Enhanced Common Service with FastAPI integration
 * Provides centralized API communication with automatic token handling,
 * error management, and retry logic
 */
class CommonService {
    constructor() {
        this.userAgent = navigator.userAgent;
        this.setupAxiosDefaults();
    }

    /**
     * Setup axios default configuration
     */
    setupAxiosDefaults() {
        // Set default timeout
        axios.defaults.timeout = API_TIMEOUT;
        
        // Set default base URL
        axios.defaults.baseURL = API_BASE_URL;
        
        // Set default headers
        axios.defaults.headers.common = {
            ...DEFAULT_HEADERS,
            'browserType': this.userAgent
        };
    }

    /**
     * Create axios instance with custom configuration
     * @param {Object} config - Axios configuration
     * @returns {Object} Axios instance
     */
    createAxiosInstance(config = {}) {
        const instance = axios.create({
            baseURL: API_BASE_URL,
            timeout: API_TIMEOUT,
            headers: {
                ...DEFAULT_HEADERS,
                'browserType': this.userAgent,
                ...config.headers
            },
            ...config
        });

        return instance;
    }

    /**
     * Retry logic for failed requests
     * @param {Function} requestFn - Request function to retry
     * @param {number} maxRetries - Maximum retry attempts
     * @param {number} delay - Delay between retries
     * @returns {Promise} Request result
     */
    async retryRequest(requestFn, maxRetries = API_RETRY_ATTEMPTS, delay = API_RETRY_DELAY) {
        let lastError;
        
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const result = await requestFn();
                return result;
            } catch (error) {
                lastError = error;
                
                // Don't retry on authentication errors or client errors (4xx)
                if (error.response?.status >= 400 && error.response?.status < 500) {
                    throw error;
                }
                
                // Don't retry on last attempt
                if (attempt === maxRetries) {
                    break;
                }
                
                // Wait before retry
                await this.delay(delay * (attempt + 1));
                
                this.log(`Retrying request (attempt ${attempt + 1}/${maxRetries})`, {
                    error: error.message,
                    status: error.response?.status
                });
            }
        }
        
        throw lastError;
    }

    /**
     * Delay utility
     * @param {number} ms - Milliseconds to delay
     * @returns {Promise} Delay promise
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Handle API responses consistently
     * @param {Object} response - Axios response
     * @returns {Object} Processed response
     */
    handleResponse(response) {
        // Log successful responses in debug mode
        this.log('API Response', {
            status: response.status,
            url: response.config.url,
            method: response.config.method
        });

        return response;
    }

    /**
     * Handle API errors consistently
     * @param {Object} error - Axios error
     * @param {boolean} showToast - Whether to show error toast
     * @returns {Object} Processed error
     */
    handleError(error, showToast = true) {
        let errorMessage = 'An unexpected error occurred';
        let errorDetails = null;

        if (error.response) {
            // Server responded with error status
            const { status, data } = error.response;
            errorMessage = data?.detail || data?.message || `Server error (${status})`;
            errorDetails = data;
            
            this.log('API Error Response', {
                status,
                url: error.config?.url,
                method: error.config?.method,
                error: errorMessage
            });
        } else if (error.request) {
            // Request was made but no response received
            errorMessage = 'Network error - please check your connection';
            
            this.log('API Network Error', {
                url: error.config?.url,
                method: error.config?.method
            });
        } else {
            // Something else happened
            errorMessage = error.message || 'Request failed';
            
            this.log('API Request Error', {
                error: errorMessage
            });
        }

        // Show toast notification if requested
        if (showToast) {
            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000
            });
        }

        return {
            success: false,
            message: errorMessage,
            error: errorDetails,
            status: error.response?.status
        };
    }

    /**
     * Make authenticated request
     * @param {string} method - HTTP method
     * @param {string} url - API endpoint
     * @param {Object} data - Request data
     * @param {Object} config - Additional config
     * @returns {Promise} Request result
     */
        async makeAuthenticatedRequest(method, url, data = null, config = {}) {
        try {
            const token = authService.getAccessToken();
            
            if (!token) {
                throw new Error('No authentication token available');
            }

            const requestConfig = {
                method: method.toUpperCase(),
                url: url.startsWith('/') ? url : `/${url}`,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    ...config.headers
                },
                ...config
            };

            // Ensure data is properly handled for GET requests
            if (data && typeof data === 'object' && Object.keys(data).length > 0) {
                if (method.toUpperCase() === 'GET') {
                    requestConfig.params = data;
                } else {
                    requestConfig.data = data;
                }
            }

            const response = await this.retryRequest(
                () => axios(requestConfig),
                config.retries || API_RETRY_ATTEMPTS,
                config.retryDelay || API_RETRY_DELAY
            );

            return this.handleResponse(response);
        } catch (error) {
            throw this.handleError(error, config.showToast !== false);
        }
    }

    /**
     * Make unauthenticated request
     * @param {string} method - HTTP method
     * @param {string} url - API endpoint
     * @param {Object} data - Request data
     * @param {Object} config - Additional config
     * @returns {Promise} Request result
     */
    async makeUnauthenticatedRequest(method, url, data = null, config = {}) {
        try {
            const requestConfig = {
                method: method.toUpperCase(),
                url: url.startsWith('/') ? url : `/${url}`,
                ...config
            };

            // Ensure data is properly handled for GET requests
            if (data && typeof data === 'object' && Object.keys(data).length > 0) {
                if (method.toUpperCase() === 'GET') {
                    requestConfig.params = data;
                } else {
                    requestConfig.data = data;
                }
            }

            const response = await this.retryRequest(
                () => axios(requestConfig),
                config.retries || API_RETRY_ATTEMPTS,
                config.retryDelay || API_RETRY_DELAY
            );

            return this.handleResponse(response);
        } catch (error) {
            throw this.handleError(error, config.showToast !== false);
        }
    }

    // ============================================================================
    // BACKWARD COMPATIBILITY METHODS (Updated to use new architecture)
    // ============================================================================

    /**
     * POST request without token (backward compatibility)
     * @param {string} apiName - API endpoint
     * @param {Object} data - Request data
     * @returns {Promise} Request result
     */
    async withOutToken(apiName, data) {
        try {
            return await this.makeUnauthenticatedRequest('POST', apiName, data);
        } catch (error) {
            // Return in old format for backward compatibility
            return {
                data: {
                    success: false,
                    message: error.message,
                    ...error.error
                }
            };
        }
    }

    /**
     * POST request without token with FormData (backward compatibility)
     * @param {string} apiName - API endpoint
     * @param {FormData} data - Form data
     * @returns {Promise} Request result
     */
    async withOutTokenFormData(apiName, data) {
        try {
            return await this.makeUnauthenticatedRequest('POST', apiName, data, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });
        } catch (error) {
            // Return in old format for backward compatibility
            return {
                data: {
                    success: false,
                    message: error.message,
                    ...error.error
                }
            };
        }
    }

    /**
     * POST request with token (backward compatibility)
     * @param {string} apiName - API endpoint
     * @param {Object} data - Request data
     * @returns {Promise} Request result
     */
    async withToken(apiName, data) {
        try {
            return await this.makeAuthenticatedRequest('POST', apiName, data);
        } catch (error) {
            // Return in old format for backward compatibility
            return {
                data: {
                    success: false,
                    message: error.message,
                    ...error.error
                }
            };
        }
    }

    /**
     * PUT request with token (backward compatibility)
     * @param {string} apiName - API endpoint
     * @param {Object} data - Request data
     * @returns {Promise} Request result
     */
    async putWithToken(apiName, data) {
        try {
            return await this.makeAuthenticatedRequest('PUT', apiName, data);
        } catch (error) {
            // Return in old format for backward compatibility
            return {
                data: {
                    success: false,
                    message: error.message,
                    ...error.error
                }
            };
        }
    }

    /**
     * GET request with token and data (backward compatibility)
     * @param {string} apiName - API endpoint
     * @param {Object} data - Request data
     * @returns {Promise} Request result
     */
    async getDataWithToken(apiName, data) {
        try {
            return await this.makeAuthenticatedRequest('GET', apiName, data);
        } catch (error) {
            // Return in old format for backward compatibility
            return {
                data: {
                    success: false,
                    message: error.message,
                    ...error.error
                }
            };
        }
    }

    /**
     * GET request with token (backward compatibility)
     * @param {string} apiName - API endpoint
     * @returns {Promise} Request result
     */
    async getWithToken(apiName) {
        try {
            return await this.makeAuthenticatedRequest('GET', apiName);
   } catch (error) {
            // Return in old format for backward compatibility
            return {
                data: {
                    success: false,
                    message: error.message,
                    ...error.error
                }
            };
        }
    }

    /**
     * DELETE request with token (backward compatibility)
     * @param {string} apiName - API endpoint
     * @param {Object} data - Request data
     * @returns {Promise} Request result
     */
    async deleteWithToken(apiName, data) {
        try {
            return await this.makeAuthenticatedRequest('DELETE', apiName, data);
   } catch (error) {
            // Return in old format for backward compatibility
            return {
                data: {
                    success: false,
                    message: error.message,
                    ...error.error
                }
            };
        }
    }

    /**
     * POST request with token and FormData (backward compatibility)
     * @param {string} apiName - API endpoint
     * @param {FormData} data - Form data
     * @returns {Promise} Request result
     */
    async withTokenFormData(apiName, data) {
        try {
            return await this.makeAuthenticatedRequest('POST', apiName, data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        } catch (error) {
            // Return in old format for backward compatibility
            return {
                data: {
                    success: false,
                    message: error.message,
                    ...error.error
                }
            };
        }
    }

    // ============================================================================
    // NEW ENHANCED METHODS
    // ============================================================================

    /**
     * Generic GET request
     * @param {string} endpoint - API endpoint
     * @param {Object} params - Query parameters
     * @param {Object} config - Request configuration
     * @returns {Promise} Request result
     */
    async get(endpoint, params = null, config = {}) {
        // Ensure params is an object and sanitize it
        const sanitizedParams = params && typeof params === 'object' ? 
            Object.fromEntries(
                Object.entries(params).filter(([_, value]) => 
                    value !== null && value !== undefined && value !== ''
                )
            ) : {};
        
        return await this.makeAuthenticatedRequest('GET', endpoint, sanitizedParams, config);
    }

    /**
     * Generic POST request
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request data
     * @param {Object} config - Request configuration
     * @returns {Promise} Request result
     */
    async post(endpoint, data = null, config = {}) {
        return await this.makeAuthenticatedRequest('POST', endpoint, data, config);
    }

    /**
     * Generic PUT request
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request data
     * @param {Object} config - Request configuration
     * @returns {Promise} Request result
     */
    async put(endpoint, data = null, config = {}) {
        return await this.makeAuthenticatedRequest('PUT', endpoint, data, config);
    }

    /**
     * Generic DELETE request
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request data
     * @param {Object} config - Request configuration
     * @returns {Promise} Request result
     */
    async delete(endpoint, data = null, config = {}) {
        return await this.makeAuthenticatedRequest('DELETE', endpoint, data, config);
    }

    /**
     * Generic PATCH request
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request data
     * @param {Object} config - Request configuration
     * @returns {Promise} Request result
     */
    async patch(endpoint, data = null, config = {}) {
        return await this.makeAuthenticatedRequest('PATCH', endpoint, data, config);
    }

    /**
     * Upload file
     * @param {string} endpoint - API endpoint
     * @param {File} file - File to upload
     * @param {Object} additionalData - Additional form data
     * @param {Function} onProgress - Progress callback
     * @returns {Promise} Upload result
     */
    async uploadFile(endpoint, file, additionalData = {}, onProgress = null) {
        const formData = new FormData();
        formData.append('file', file);
        
        // Add additional data to form
        Object.keys(additionalData).forEach(key => {
            formData.append(key, additionalData[key]);
        });

        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };

        if (onProgress) {
            config.onUploadProgress = (progressEvent) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                onProgress(percentCompleted);
            };
        }

        return await this.makeAuthenticatedRequest('POST', endpoint, formData, config);
    }

    /**
     * Download file
     * @param {string} endpoint - API endpoint
     * @param {string} filename - Filename for download
     * @param {Object} params - Query parameters
     * @returns {Promise} Download result
     */
    async downloadFile(endpoint, filename, params = null) {
        const config = {
            responseType: 'blob',
            showToast: false
        };

        const response = await this.makeAuthenticatedRequest('GET', endpoint, params, config);
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return response;
    }

    /**
     * Logging utility
     * @param {string} message - Log message
     * @param {Object} data - Additional data
     */
    log(message, data = null) {
        if (DEBUG_MODE) {
            console.log(`[CommonService] ${message}`, data);
        }
    }
}

// Create singleton instance
const commonService = new CommonService();

// Export both the class and instance for flexibility
export { CommonService };
export { commonService };

// Export default for backward compatibility
export default commonService;

